import React, { useEffect, useState } from 'react';
import { useStateSelector } from '../../store/selectors';
import { zipCodeConvert } from '../../utils/zipcodeConvertor/zipcodeConvertor';
import { MarkerData } from '../../components/LandscapeMap/LandscapeMapTypes';
import Layout from '../../components/Layout';
import { useSearchParams } from 'react-router-dom';
import ZoomSettings from '../../components/LandscapeMap/ZoomSettings/ZoomSettings';
import ListContainer from './ListContainer/ListContainer';
import { ShapeType } from '../../components/LandscapeMap/Markers/Shapes/MarkersWrapper';
import { Card, Col, Row } from 'reactstrap';
import './LandscapeMapContainer.scss';
import LandscapeMapContainer, {
    Region,
    regionConfigs,
} from './LandscapeMapContainer';
import InfoTooltip from '../../components/info-tooltip/info-tooltip';
import { PracticeFocusOptionsMapping } from '../../utils/constants/PracticeFocus.enum';

export type PracticeType =
    | 'Engaged'
    | 'Tagged'
    | 'NewOpportuinity'
    | 'ProcessCompleted'
    | 'BuyerMissedDeadline'
    | 'SellerDeclined'
    | 'BuyerDeclined'
    | 'OurExistingPractices';

export interface PracticeTypeConfig {
    name: string;
    color: string;
    description?: string;
}

const practiceTypes: Record<PracticeType, PracticeTypeConfig> = {
    Engaged: { name: 'Engaged', color: '#008000' },
    Tagged: { name: 'Tagged', color: '#3e0aa5' },
    NewOpportuinity: {
        name: 'New Opportuinity',
        color: '#1f2bdf',
    },
    ProcessCompleted: {
        name: 'Process Completed',
        color: '#4b61ad',
        description: 'Tooltip Description',
    },
    BuyerMissedDeadline: {
        name: 'Buyer Missed Deadline',
        color: '#d3ae00',
    },
    SellerDeclined: {
        name: 'Seller Declined',
        color: '#e85d03',
    },
    BuyerDeclined: {
        name: 'Buyer Declined',
        color: '#d33b12',
    },
    OurExistingPractices: {
        name: 'Our Existing Practices',
        color: '#000',
    },
};

export function getPracticeColorByIndex(index: number): string {
    const values = Object.values(practiceTypes);
    return values[index % values.length].color;
}

export function getShapeByIndex(index: number): ShapeType {
    const shapes: ShapeType[] = [
        ShapeType.Circle,
        ShapeType.Rectangle,
        ShapeType.Diamond,
        ShapeType.Pentagon,
        ShapeType.Octagon,
        ShapeType.Star5,
        ShapeType.Star6,
        ShapeType.Triangle,
        ShapeType.Star4,
    ];
    return shapes[index % shapes.length];
}

const LandscapeMapDemoPage = () => {
    const axios = useStateSelector((x) => x.core.axios);
    const [practices, setPractices] = useState([]);
    const [markers, setMarkers] = useState<MarkerData[]>([]);
    const [selectedMarkers, setSelectedMarkers] = useState<MarkerData[]>([]);
    const [searchParams] = useSearchParams({});
    const [currentZoom, setCurrentZoom] = useState(
        regionConfigs[Region.Mainland].defaultZoom
    );
    const [markerVisibilityZoom, setMarkerVisibilityZoom] = useState(16);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [isIdNdaSigned, setIsIdNdaSigned] = useState(true);
    const [isMockPositionEnabled, setIsMockPositionEnabled] = useState(true);

    useEffect(() => {
        getMarkers();
    }, []);

    const getMarkers = () => {
        const practicesCountString = searchParams.get('practicesCount');
        const practicesCount = Number(practicesCountString) || 25;
        initializeMarkers(practicesCount);
    };

    const renderColorsDescription = () => {
        return Object.entries(practiceTypes).map((type) => {
            const config = type[1];
            return (
                <div className="legend-item">
                    <div
                        className="item-bullet"
                        style={{
                            background: config.color,
                        }}></div>
                    <div className="item-name">{config.name}</div>
                    {config.description ? (
                        <InfoTooltip
                            text={config.description}
                            idText={`legend-${type[0]}-item`}
                        />
                    ) : (
                        <></>
                    )}
                </div>
            );
        });
    };

    const renderShapesDescription = () => {
        return PracticeFocusOptionsMapping.map((practiceFocus) => {
            const name = practiceFocus.label;
            const icon = practiceFocus.mapMarker;
            if (name === 'GP') {
                return (
                    <div className="legend-item">
                        <div
                            className="item-bullet"
                            style={{
                                background: '#7d7d7d',
                            }}></div>
                        <div className="item-name">{name}</div>
                    </div>
                );
            }
            return (
                <div className="legend-item">
                    <div className="item-bullet">{icon}</div>
                    <div className="item-name">{name}</div>
                </div>
            );
        });
    };

    useEffect(() => {
        const updatedMarkers = practices
            .map((practice, index) => {
                const position = getMarkerPosition(practice.zipCode, index);
                if (!position) return null;
                return {
                    id: practice.id,
                    showTooltip: true,
                    name: practice.name,
                    position: position,
                    color: getPracticeColorByIndex(index),
                    shape: getShapeByIndex(index),
                    fteDVMs: getFteDvms(practice, index),
                    isIdNdaSigned: isIdNdaSigned,
                    onClick: () => {
                        handleMarkerClick(practice.id);
                    },
                };
            })
            .filter(Boolean) as MarkerData[];
        setMarkers(updatedMarkers);
    }, [isIdNdaSigned, isMockPositionEnabled, practices]);

    const handleMarkerClick = (id: number) => {
        const selectedMarker = markers.find((marker) => marker.id === id);
        setSelectedMarkers([selectedMarker]);
    };

    const markersSizeExampleCoordinates = [
        [44, -119],
        [44, -116],
        [44, -113],
        [44, -110],
        [44, -107],
        [44, -104],
        [44, -101],
        [44, -98],
        [44, -95],
        [44, -92],
        [40, -119],
        [40, -116],
        [40, -113],
        [40, -110],
        [40, -107],
        [40, -104],
        [40, -101],
        [40, -98],
        [40, -95],
        [40, -92],
        [36, -119],
        [36, -116],
        [36, -113],
        [36, -110],
        [36, -107],
        [36, -104],
        [36, -101],
        [36, -98],
        [36, -95],
        [36, -92],
        [32, -119],
        [32, -116],
        [32, -113],
        [32, -110],
        [32, -107],
        [32, -104],
        [32, -101],
        [32, -98],
        [32, -95],
        [32, -92],
        [28, -119],
        [28, -116],
        [28, -113],
        [28, -110],
        [28, -107],
        [28, -104],
        [28, -101],
        [28, -98],
        [28, -95],
        [28, -92],
    ];

    const getMarkerPosition = (
        zipCode: string,
        i: number
    ): [number, number] | null => {
        if (isMockPositionEnabled && markersSizeExampleCoordinates[i]) {
            return [
                markersSizeExampleCoordinates[i][0],
                markersSizeExampleCoordinates[i][1],
            ];
        }
        try {
            return zipCodeConvert(zipCode);
        } catch {
            return null;
        }
    };

    const getFteDvms = (practice: any, i: number) => {
        if (isMockPositionEnabled) return (i % 100) + 1;
        if (practice?.noOfFteDvms > 0) return practice.noOfFteDvms;
        return 1;
    };

    const initializeMarkers = (count: number) => {
        axios.get(`/api/practice/${count}`).then((response) => {
            if (response.status === 200) {
                setPractices(response.data);
                const ms = response.data
                    .map((p: any, i: number) => {
                        const pos = getMarkerPosition(p.zipCode, i);
                        if (!pos) return null;
                        return {
                            id: p.id,
                            name: p.name,
                            position: pos,
                            type: getPracticeColorByIndex(i),
                            shape: getShapeByIndex(i),
                            fteDVMs: getFteDvms(p, i),
                            isIdNdaSigned: isIdNdaSigned,
                        };
                    })
                    .filter(Boolean) as MarkerData[];
                setMarkers(ms);
            }
        });
    };

    return (
        <Layout>
            <ZoomSettings
                currentZoom={currentZoom}
                markerVisibilityZoom={markerVisibilityZoom}
                setMarkerVisibilityZoom={setMarkerVisibilityZoom}
                isIdNdaSigned={isIdNdaSigned}
                setIsIdNdaSigned={setIsIdNdaSigned}
                isMockPositionEnabled={isMockPositionEnabled}
                setIsMockPositionEnabled={setIsMockPositionEnabled}
            />
            <Card className="main-card p-4 pb-0 mb-4">
                <Row>
                    <LandscapeMapContainer
                        currentZoom={currentZoom}
                        setCurrentZoom={setCurrentZoom}
                        markers={markers}
                        selectedMarkers={selectedMarkers}
                        setSelectedMarkers={setSelectedMarkers}
                        isFullScreen={isFullScreen}
                        setIsFullScreen={setIsFullScreen}
                        setMarkers={setMarkers}
                        markerVisibilityZoom={markerVisibilityZoom}
                        renderColorsDescription={renderColorsDescription}
                        renderShapesDescription={renderShapesDescription}
                        colMd={isFullScreen ? 12 : 6}></LandscapeMapContainer>
                    <Col md={isFullScreen ? 12 : 6}>
                        <ListContainer
                            setSelectedMarker={setSelectedMarkers}
                            markers={markers}
                        />
                    </Col>
                </Row>
            </Card>
        </Layout>
    );
};

export default LandscapeMapDemoPage;
